/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2023
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
/**
 * @class GridCellColRowSpanHelper
 * @classdesc This class calculates colspan and rowspan values of a grid cell
 * Each cell has its own colspan and rowspan value
 * If there are N columns on the same row that have the same value then colspan = N columns - 1
 * If there are N rows on the same column that have the same value on N rows then rowspan = N rows - 1
 * It also calculates gridCellColumnWidth gridCellRowHeight base on the calculated colspan and rowspan values.
 */
define([
], (
) => {
	const COLSPAN_VALUE_TYPES = ['corner', 'column'];
	const ROWSPAN_VALUE_TYPES = ['corner', 'row'];
	const USE_VALUE = 'useValue';

	/* 1. Corner cells support both rowspan and colspan values
	 *
	 * 2. Summary cells may have both rowspan and colspan values
	 *
	 * 3. Non-summary/Non-corner are allow to have only one colspan or rowspan value but not both.
	 * When they locate on row headers they can have colspan values
	 * Otherwise if they locate on column headers they can have rowspan values
	 *
	 * For non-summary cells calculations for colspan and rowspan are very simple, loop until find a value that does not match to exit the loop to stop increment colspan/rowspan
	 * Topmost header rows: cells have colspans only
	 * Leftmost header columns: cells have rowspans only
	 *
	 * For Summary cells, they can have either colspan or rowspan on an edge. This makes it more complicated to calculate spanning values for these types of cells
	 *
	 * Virtual react grid, there are different parts of a crosstab, namely the "corner", the
  	 * "fixed columns" (i.e. row headings), the "fixed rows" (i.e. column headings) and the data.
  	 *
  	 * |             |                                   |     ^
  	 * |  <Corner>   |    ...    |    ...    |    ...    |  Fixed rows
  	 * |             | ... | ... | ... | ... | ... | ... |     v
  	 * |-------------|-----|-----|-----|-----|-----|-----|
  	 * | <  Fixed  > | ... | ... | ### | ### | ### | ### |
  	 * | < Columns > |     | ... | ### | ### | ### | ### |
  	 * |             |-----|-----|-----|-----|-----|-----|
  	 * |             | ... | ... | ### |   <Data>  | ### |
  	 * |             |     | ... | ### | ### | ### | ### |
  	 * |             |-----|-----|-----|-----|-----|-----|
  	 * |             | ... | ... | ### | ### | ### | ### |
  	 * |             |     | ... | ### | ### | ### | ### |
  	 *
	 * FixedColumns: the number of left most column headers not including data cells. For example Camping Equipment is a column header cell
	 * FixedRows: the number of top most row headers not including data cells. For example Camping Equipment is a row header cell
	 *
  	 * In crosstabs, cells are merged in various ways, namely:
  	 *
  	 * 1) Corner - The corner is always exactly one cell, so its rowSpan is the number of rows in
  	 * the fixed rows, and its colSpan is the number of columns in the fixed columns.
  	 *
  	 * 2) Fixed rows and columns - There are two sorts of merging in the fixed rows and columns:
  	 *    a) Nesting. In hierarchical rendering, parents merge the full breadth of their children.
  	 *       So, in fixed rows, they have a row span, e.g.
  	 *
  	 *       |               2007              |               2008              |
  	 *       | Spring | Summer | Fall | Winter | Spring | Summer | Fall | Winter |
  	 *
  	 *       And, in fixed columns they have a col span, e.g.
  	 *
  	 *       |      | Spring |
  	 *       | 2007 | Summer |
  	 *       |      | Fall   |
  	 *       |      | Winter |
  	 *       |------|--------|
  	 *       |      | Spring |
  	 *       | 2008 | Summer |
  	 *       |      | Fall   |
  	 *       |      | Winter |
  	 *
  	 *    b) Summaries. These merge across nesting levels. So, in fixed rows, they have a col span,
  	 *       e.g.
  	 *
  	 *       2010                         | Summary |
  	 *       |----------------------------|         |
  	 *       Winter             | Summary |         |
  	 *       -------------------|         |         |
  	 *       | Friday | Summary |         |         |
  	 *       |--------|---------|---------|---------|
  	 *
  	 *       And likewise, in fixed columns, they have a row span, e.g.
  	 *
  	 *       | 2010 | Winter |---------|
  	 *       |      |        | Friday  |
  	 *       |      |        |---------|
  	 *       |      |        | Summary |
  	 *       |      |--------|---------|
  	 *       |      | Summary          |
  	 *       |------|------------------|
  	 *       | Summary                 |
  	 *       |-------------------------|
  	 *
  	 * 3) Data - there is no merging of data cells.
	 *
	 * Example of summary colspan/rowspan
	 *       | 2010 | Winter |-----------------|
  	 *       |      |        | Revenue         |
  	 *       |      |        |-----------------|
  	 *       |      |        | Planned revenue |
  	 *       |      |--------|-----------------|
  	 *       |      | Summary| Revenue         | Note: Summary cell has rowspan = 1 to covers for both Revenue and Planned revenue row
	 *		 |      |        | Planned revenue |
  	 *       |------|--------| Revenue         |
  	 *       | Summary       | Planned revenue | Note: Summary cell has rowspan = 1 to covers for both Revenue and Planned revenue row
  	 *       |---------------------------------|                        colspan = 1 to cover 2 summary cells
  	 *
	 * columnEdgeMaxRowIndex: the total number of data rows for the crosstab. This number includes the number of top most row headers
	 * rowEdgeMaxColumnIndex: the total number of data columns for the crosstab. This number includes the number of left most column headers
	 *
	 */
	return class GridCellColRowSpanHelper {
		constructor({ gridInfo, cellInfo, gridCellProps, crosstabGridCellRenderers, handlers }) {
			this.gridInfo = gridInfo;
			this.cellInfo = cellInfo;
			this.gridCellProps = gridCellProps;
			this.crosstabGridCellRenderers = crosstabGridCellRenderers;
			const keys = Object.keys(handlers);
			keys.forEach(key => {
				this[key] = handlers[key];
			});
			this.cellInfo.dataValue = this.cellInfo.dataValue || this.gridInfo.dataProvider.getValue(this.cellInfo.currentRow, this.cellInfo.currentCol);
		}

		_getColSpanInfo () {
			const { dataProvider, fixedRows, fixedColumnWidth } = this.gridInfo;
			// Note: Corner cells do not provide dataValue
			const { currentRow, currentCol, rowEdgeMaxColumnIndex, dataValue, dataCellValue, useValue, isSummary: isSummaryCell, valueType } = this.cellInfo;
			let nextColIndex = currentCol + 1;
			let gridCellColumnWidth = parseInt(this.gridCellProps.style.width, 10);
			if (isSummaryCell || COLSPAN_VALUE_TYPES.includes(valueType)) {
				const summaryCellUniqueSpanKey = this.crosstabGridCellRenderers.isSummary(dataValue) && this._geUniqueColSpanKeyForSummaryCell({ colIndex: currentCol, dataValue });
				//Increase the cell span and the cell width IF the value at currentCol+span = value at currentCol.
				while (nextColIndex <= rowEdgeMaxColumnIndex && this._isMatchedBaseOnUniqueValueForEdgeCell({ colIndex: nextColIndex, dataCellValue, dataProvider, rowIndex: currentRow, useValue, isSummaryCell })) {
					if (this._isOccupyingMoreThanOneCell({ colIndex: nextColIndex, summaryCellUniqueSpanKey, isForRowSpan: false })) {
						gridCellColumnWidth += this.getColumnWidth(dataProvider, fixedRows - 1, nextColIndex, fixedColumnWidth);
						nextColIndex++;
					} else {
						// Next cell do not have same value so break out of while loop since this cell is no part of colSpan
						break;
					}
				}
			}
			return { colSpan: nextColIndex - currentCol - 1, gridCellColumnWidth };
		}

		_getRowSpanInfo () {
			const { dataProvider, fixedColumns, fixedRowHeight } = this.gridInfo;
			// Note: Corner cells do not provide dataValue
			const { currentRow, currentCol, columnEdgeMaxRowIndex, dataValue, dataCellValue, useValue, isSummary: isSummaryCell, valueType } = this.cellInfo;
			let nextRowIndex = currentRow + 1;
			let gridCellRowHeight = parseInt(this.gridCellProps.style.height, 10);
			if (isSummaryCell || ROWSPAN_VALUE_TYPES.includes(valueType)) {
				const summaryCellUniqueSpanKey = this.crosstabGridCellRenderers.isSummary(dataValue) && this._geUniqueRowSpanKeyForSummaryCell({ rowIndex: currentRow, dataValue });
				//Increase the row span and the cell height IF the value at rowIndex+span = value at rowIndex.
				while (nextRowIndex <= columnEdgeMaxRowIndex && this._isMatchedBaseOnUniqueValueForEdgeCell({ colIndex: currentCol, dataCellValue, dataProvider, rowIndex: nextRowIndex, useValue, isSummaryCell })) {
					if (this._isOccupyingMoreThanOneCell({ rowIndex: nextRowIndex, summaryCellUniqueSpanKey, isForRowSpan: true })) {
						gridCellRowHeight += this.getRowHeight(dataProvider, nextRowIndex, fixedColumns - 1, fixedRowHeight);
						nextRowIndex++;
					} else {
						// Cell on next row at same column index do not have same value so break out of while loop since this cell is no part of rowSpan
						break;
					}
				}
			}
			return { rowSpan: nextRowIndex - currentRow - 1, gridCellRowHeight };
		}

		/*
		 * Returns a boolean indicating whether the data value at cell rowIndex and colIndex has span to occupy more than one cell
		 * in either column or row direction
		 */
		_isOccupyingMoreThanOneCell ({ rowIndex, colIndex, summaryCellUniqueSpanKey, isForRowSpan }) {
			const { dataProvider } = this.gridInfo;
			const { currentRow, currentCol, dataValue, valueType } = this.cellInfo;
			let nextCellValue;
			let nextSummaryCellUniqueSpanKey;
			if (isForRowSpan) {
				nextCellValue = dataProvider.getValue(rowIndex, currentCol);
				nextSummaryCellUniqueSpanKey = this.crosstabGridCellRenderers.isSummary(nextCellValue) && this._geUniqueRowSpanKeyForSummaryCell({ rowIndex, dataValue: nextCellValue });
			} else {
				nextCellValue = dataProvider.getValue(currentRow, colIndex);
				nextSummaryCellUniqueSpanKey = this.crosstabGridCellRenderers.isSummary(nextCellValue) && this._geUniqueColSpanKeyForSummaryCell({ colIndex, dataValue: nextCellValue });
			}
			const hasSameValue = this._isValidCellValue({ dataCellValue: nextCellValue, valueType }) && this.crosstabGridCellRenderers.getDataCellValue(dataValue) === this.crosstabGridCellRenderers.getDataCellValue(nextCellValue);
			return (hasSameValue && (!summaryCellUniqueSpanKey || summaryCellUniqueSpanKey === nextSummaryCellUniqueSpanKey));
		}

		/* Example of summary colspan/rowspan unique keys
		 *       | 2010 | Winter |-----------------|
		 *       |      |        | Revenue         |
		 *       |      |        |-----------------|
		 *       |      |        | Planned revenue |
		 *       |      |--------|-----------------|
		 *       |      | Summary| Revenue         | Note: Summary cell has rowspan = 1 to covers for both Revenue and Planned revenue row
		 *	 |      |        | Planned revenue |
		 *       |------|--------| Revenue         |
		 *       | Summary       | Planned revenue | Note: Summary cell has rowspan = 1 to covers for both Revenue and Planned revenue row
		 *	 |---------------------------------|
		 *
		 * data = [
		 * 		{
		 *			2010, Summary, Revenue						Row N
		 *		},
		 *		{
		 *			2010, Summary, Planned revenue 				Row N + 1
		 *		},
		 *		{
		 *			Summary, Summary, Revenue					Row N + 2
		 *		},
		 *		{
		 *			Summary, Summary, Planned revenue      		Row N + 3
		 *		}
		 *	]
		 *
		 * Unique key for row N is '2010,Summary'
		 * Unique key for row N + 1 is '2010,Summary'
		 * Unique key for row N + 2 is 'Summary,Summary'
		 * Unique key for row N + 3 is 'Summary,Summary'
		 *
		 * '2010,Summary' is the same for row N and N + 1 result ==> rowspan = 1 to span 2 rows. rowspan does not include row N + 2 nor N + 3 becuase these rows
		 * have different unique key 'Summary,Summary'. where '2010,Summary' != 'Summary,Summary'
		 *
		 * Likewise 'Summary,Summary' has rowspan = 1 to cover both rows N + 2 and N + 3
		 * 'Summary,Summary' has colspan = 1 to cover both same column at rows N + 2 and N + 3
		 *
		 * The same example is also true for top row headers
		 * Base on this example, given a cell at row/col iterate backwards to calculate unique keys
		 *
		 * _geUniqueColSpanKeyForSummaryCell and _geUniqueRowSpanKeyForSummaryCell calculate unique summary col/row span keys
		 */
		// Iterate backwards on rows for same column index
		_geUniqueColSpanKeyForSummaryCell ({ colIndex, dataValue }) {
			let key;
			const { currentRow, scope } = this.cellInfo;
			if (scope === 'col') {
				const { dataProvider } = this.gridInfo;
				const keys = [this.crosstabGridCellRenderers.getDataCellValue(dataValue, USE_VALUE)];
				let rowIndex = currentRow - 1;
				while (rowIndex >= 0) {
					const cellValue = dataProvider.getValue(rowIndex, colIndex);
					if (this.crosstabGridCellRenderers.isSummary(cellValue)) {
						keys.push(this.crosstabGridCellRenderers.getDataCellValue(cellValue, USE_VALUE));
					} else {
						break;
					}
					--rowIndex;
				}
				key = keys.join(',');
			}
			return key;
		}

		// Iterate backwards on columns for same row index
		_geUniqueRowSpanKeyForSummaryCell ({ rowIndex, dataValue }) {
			let key;
			const { currentCol, scope } = this.cellInfo;
			if (scope === 'row') {
				const { dataProvider } = this.gridInfo;
				const keys = [this.crosstabGridCellRenderers.getDataCellValue(dataValue, USE_VALUE)];
				let colIndex = currentCol - 1;
				while (colIndex >= 0) {
					const cellValue = dataProvider.getValue(rowIndex, colIndex);
					if (this.crosstabGridCellRenderers.isSummary(cellValue)) {
						keys.push(this.crosstabGridCellRenderers.getDataCellValue(cellValue, USE_VALUE));
					} else {
						break;
					}
					--colIndex;
				}
				key = keys.join(',');
			}
			return key;
		}

		/**
		 * Return a boolean flag indicating whether the value is match base on the useValue whenever possible
		 * If there is no useValue then the display value is use to match the value at rowIndex and columnIndex
		 *
		 * @param {integer} colIndex, column index of current cell
		 * @param {string} dataCellValue
		 * @param {object} dataProvider
		 * @param {integer} rowIndex, row index of current cell
		 * @param {string} [useValue], optional useValue
		 * @param {boolean} isSummaryCell
		 * @return {boolean} true if value to verify is matched at rowIndex and columnIndex, else return false
		 */
		_isMatchedBaseOnUniqueValueForEdgeCell ({ colIndex, dataCellValue, dataProvider, rowIndex, useValue, isSummaryCell }) {
			const dataValue = dataProvider.getValue(rowIndex, colIndex);
			let summaryCell = isSummaryCell;

			if (isSummaryCell) {
				summaryCell = this.crosstabGridCellRenderers.isSummary(dataValue);
			}

			// skip for summary
			if (!summaryCell) {
				return ((useValue && this.crosstabGridCellRenderers.getDataCellValue(dataValue, 'useValue') === useValue) || (!useValue && this.crosstabGridCellRenderers.getDataCellValue(dataValue) === dataCellValue));
			}
			return true;
		}

		_isValidCellValue ({ dataCellValue, valueType }) {
			if (!dataCellValue || !valueType) {
				return false;
			}
			return dataCellValue.type === valueType;
		}

		getSpanInfo () {
			return {
				...this._getColSpanInfo(),
				...this._getRowSpanInfo()
			};
		}

		static getSpanInfo({ gridInfo, cellInfo, gridCellProps, crosstabGridCellRenderers, handlers }) {
			return new GridCellColRowSpanHelper({ gridInfo, cellInfo, gridCellProps, crosstabGridCellRenderers, handlers }).getSpanInfo();
		}
	};
});
